import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./OrderActions.css";

const ACTIVITIES = [
  {
    id: "brush_teeth",
    title: "Lavarse los dientes",
    minSteps: 3,
    steps: [
      "Coger el cepillo de dientes",
      "Echar pasta en el cepillo",
      "Cepillarse los dientes",
      "Enjuagarse la boca",
      "Limpiar y guardar el cepillo",
    ],
  },
  {
    id: "wash_hands",
    title: "Lavarse las manos",
    minSteps: 3,
    steps: [
      "Abrir el grifo",
      "Mojarse las manos",
      "Echarse jabón",
      "Frotarse las manos",
      "Aclararse las manos",
      "Cerrar el grifo y secarse",
    ],
  },
  {
    id: "get_dressed",
    title: "Vestirse",
    minSteps: 3,
    steps: [
      "Elegir la ropa",
      "Ponerse la ropa interior",
      "Ponerse la camiseta",
      "Ponerse el pantalón",
      "Ponerse los calcetines",
      "Ponerse los zapatos",
    ],
  },
  {
    id: "prepare_backpack",
    title: "Preparar la mochila",
    minSteps: 3,
    steps: [
      "Consultar qué asignaturas hay",
      "Buscar los libros necesarios",
      "Guardar los cuadernos",
      "Meter el estuche",
      "Comprobar que no falta nada",
      "Cerrar la mochila",
    ],
  },
  {
    id: "make_sandwich",
    title: "Preparar un bocadillo",
    minSteps: 3,
    steps: [
      "Preparar los ingredientes",
      "Abrir el pan",
      "Colocar el relleno",
      "Cerrar el bocadillo",
      "Guardar los ingredientes",
      "Limpiar la zona",
    ],
  },
  {
    id: "set_table",
    title: "Poner la mesa",
    minSteps: 3,
    steps: [
      "Limpiar la mesa",
      "Colocar los platos",
      "Colocar los cubiertos",
      "Poner los vasos",
      "Colocar las servilletas",
      "Llevar la comida a la mesa",
    ],
  },
  {
    id: "plant_seed",
    title: "Plantar una semilla",
    minSteps: 3,
    steps: [
      "Preparar una maceta",
      "Echar tierra en la maceta",
      "Hacer un pequeño agujero",
      "Colocar la semilla",
      "Cubrir la semilla con tierra",
      "Regar la tierra",
    ],
  },
  {
    id: "make_lemonade",
    title: "Preparar limonada",
    minSteps: 3,
    steps: [
      "Preparar los ingredientes",
      "Cortar los limones",
      "Exprimir los limones",
      "Añadir agua",
      "Añadir azúcar",
      "Remover la mezcla",
    ],
  },
  {
    id: "take_shower",
    title: "Ducharse",
    minSteps: 3,
    steps: [
      "Preparar la toalla y la ropa",
      "Abrir el agua",
      "Mojarse el cuerpo",
      "Lavarse con jabón",
      "Aclararse",
      "Cerrar el agua y secarse",
    ],
  },
  {
    id: "prepare_omelette",
    title: "Preparar una tortilla de patatas",
    minSteps: 4,
    steps: [
      "Pelar y cortar las patatas",
      "Calentar aceite en una sartén",
      "Cocinar las patatas",
      "Batir los huevos",
      "Mezclar los huevos con las patatas",
      "Cocinar la mezcla y darle la vuelta",
    ],
  },
  {
    id: "go_to_school",
    title: "Prepararse para ir al colegio",
    minSteps: 4,
    steps: [
      "Levantarse de la cama",
      "Asearse",
      "Vestirse",
      "Desayunar",
      "Preparar la mochila",
      "Salir de casa",
    ],
  },
  {
    id: "wash_clothes",
    title: "Poner una lavadora",
    minSteps: 4,
    steps: [
      "Separar la ropa",
      "Meter la ropa en la lavadora",
      "Añadir detergente",
      "Elegir el programa",
      "Poner la lavadora en marcha",
      "Sacar y tender la ropa",
    ],
  },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 6,
    itemCount: 3,
  },

  medium: {
    label: "Media",
    totalRounds: 8,
    itemCount: 4,
  },

  hard: {
    label: "Difícil",
    totalRounds: 10,
    itemCount: 6,
  },
};

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const selectActivitySteps = (activity, itemCount) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 3, 3),
    activity.steps.length
  );

  if (safeItemCount === activity.steps.length) {
    return activity.steps.map((text, index) => ({
      id: `${activity.id}-${index}`,
      text,
      correctPosition: index,
    }));
  }

  const selectedIndexes = Array.from(
    { length: safeItemCount },
    (_, index) =>
      Math.round(
        (index * (activity.steps.length - 1)) /
          (safeItemCount - 1)
      )
  );

  const uniqueIndexes = [
    ...new Set(selectedIndexes),
  ];

  while (uniqueIndexes.length < safeItemCount) {
    const missingIndex = activity.steps.findIndex(
      (_, index) => !uniqueIndexes.includes(index)
    );

    if (missingIndex === -1) {
      break;
    }

    uniqueIndexes.push(missingIndex);
    uniqueIndexes.sort(
      (first, second) => first - second
    );
  }

  return uniqueIndexes.map(
    (originalIndex, sequenceIndex) => ({
      id: `${activity.id}-${originalIndex}`,
      text: activity.steps[originalIndex],
      correctPosition: sequenceIndex,
    })
  );
};

const createRound = ({
  itemCount,
  previousActivityId = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 3, 3),
    6
  );

  const availableActivities = ACTIVITIES.filter(
    (activity) =>
      activity.steps.length >= safeItemCount &&
      activity.id !== previousActivityId
  );

  const activity =
    availableActivities[
      Math.floor(
        Math.random() *
          availableActivities.length
      )
    ];

  const orderedSteps = selectActivitySteps(
    activity,
    safeItemCount
  );

  let shuffledSteps = shuffleArray(orderedSteps);

  const remainsOrdered = shuffledSteps.every(
    (step, index) =>
      step.id === orderedSteps[index].id
  );

  if (remainsOrdered && shuffledSteps.length > 1) {
    shuffledSteps = [
      shuffledSteps[1],
      shuffledSteps[0],
      ...shuffledSteps.slice(2),
    ];
  }

  return {
    id: `${Date.now()}-${Math.random()}`,
    activityId: activity.id,
    title: activity.title,
    orderedSteps,
    shuffledSteps,
  };
};

function OrderActions() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [availableSteps, setAvailableSteps] =
    useState([]);

  const [selectedSteps, setSelectedSteps] =
    useState([]);

  const [perfectRounds, setPerfectRounds] =
    useState(0);

  const [correctPositions, setCorrectPositions] =
    useState(0);

  const [positionErrors, setPositionErrors] =
    useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const totalEvaluatedPositions =
    correctPositions + positionErrors;

  const accuracy = calculateAccuracy(
    correctPositions,
    totalEvaluatedPositions
  );

  const errors = calculateErrorCount(positionErrors);

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const prepareRound = (nextRound) => {
    setRound(nextRound);
    setAvailableSteps(nextRound.shuffledSteps);
    setSelectedSteps([]);
    setRoundStartTime(Date.now());
  };

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const nextConfig = {
      ...resolvedConfig,
      difficultyKey,
      totalRounds:
        Number(resolvedConfig.totalRounds) || 6,
      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) || 3,
          3
        ),
        6
      ),
    };

    const firstRound = createRound(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setAvailableSteps(firstRound.shuffledSteps);
    setSelectedSteps([]);

    setStarted(true);
    setFinished(false);

    setPerfectRounds(0);
    setCorrectPositions(0);
    setPositionErrors(0);
    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const selectStep = (step) => {
    setAvailableSteps((current) =>
      current.filter(
        (availableStep) =>
          availableStep.id !== step.id
      )
    );

    setSelectedSteps((current) => [
      ...current,
      step,
    ]);
  };

  const removeSelectedStep = (step, index) => {
    setSelectedSteps((current) =>
      current.filter(
        (_, selectedIndex) =>
          selectedIndex !== index
      )
    );

    setAvailableSteps((current) => [
      ...current,
      step,
    ]);
  };

  const moveSelectedStep = (
    index,
    direction
  ) => {
    setSelectedSteps((current) => {
      const newIndex = index + direction;

      if (
        newIndex < 0 ||
        newIndex >= current.length
      ) {
        return current;
      }

      const reordered = [...current];

      [reordered[index], reordered[newIndex]] = [
        reordered[newIndex],
        reordered[index],
      ];

      return reordered;
    });
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const confirmSequence = () => {
    if (
      !round ||
      selectedSteps.length !==
        round.orderedSteps.length
    ) {
      return;
    }

    const responseTime =
      Date.now() - roundStartTime;

    const roundCorrectPositions =
      selectedSteps.reduce(
        (total, step, index) =>
          total +
          (step.id ===
          round.orderedSteps[index].id
            ? 1
            : 0),
        0
      );

    const roundPositionErrors =
      round.orderedSteps.length -
      roundCorrectPositions;

    const isPerfect =
      roundPositionErrors === 0;

    setCorrectPositions(
      (current) =>
        current + roundCorrectPositions
    );

    setPositionErrors(
      (current) =>
        current + roundPositionErrors
    );

    if (isPerfect) {
      setPerfectRounds(
        (current) => current + 1
      );
    }

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousActivityId: round.activityId,
    });

    setRoundIndex(nextRoundIndex);
    prepareRound(nextRound);
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Ordenar acciones"
        description="Ordena correctamente los pasos necesarios para realizar diferentes actividades cotidianas."
        areas={[
          "Planificación",
          "Secuenciación temporal",
          "Memoria de trabajo",
          "Razonamiento lógico",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 3,
            max: 24,
            defaultValue: 8,
          }),

          GameFields.itemCount({
            label: "Pasos por actividad",
            min: 3,
            max: 6,
            defaultValue: 4,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="order-actions-page">
        <div className="order-actions-results">
          <GameResults
            title="Resultados"
            correctAnswers={perfectRounds}
            totalAnswers={totalRounds}
            accuracy={accuracy}
            errors={errors}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="order-actions-results-grid">
            <div className="order-actions-result-card">
              <span>Secuencias perfectas</span>

              <strong>
                {perfectRounds}/{totalRounds}
              </strong>
            </div>

            <div className="order-actions-result-card">
              <span>Pasos bien colocados</span>
              <strong>{correctPositions}</strong>
            </div>

            <div className="order-actions-result-card">
              <span>Errores de posición</span>
              <strong>{positionErrors}</strong>
            </div>

            <div className="order-actions-result-card">
              <span>Precisión posicional</span>
              <strong>
                {Math.round(accuracy)}%
              </strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="order-actions-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-actions-page">
      <main className="order-actions-card">
        <header className="order-actions-header">
          <div>
            <p className="order-actions-area">
              Planificación
            </p>

            <h1>Ordenar acciones</h1>

            <p>
              Coloca los pasos en el orden
              adecuado.
            </p>
          </div>

          <div className="order-actions-score">
            <span>Secuencias perfectas</span>
            <strong>{perfectRounds}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="order-actions-game">
          <div className="order-actions-activity">
            <span>Actividad</span>
            <h2>{round?.title}</h2>
          </div>

          <div className="order-actions-sections">
            <div className="order-actions-section">
              <div className="order-actions-section-header">
                <h3>Pasos disponibles</h3>

                <span>
                  {availableSteps.length}
                </span>
              </div>

              <div className="order-actions-list">
                {availableSteps.length > 0 ? (
                  availableSteps.map((step) => (
                    <button
                      type="button"
                      key={step.id}
                      className="order-actions-available-item"
                      onClick={() =>
                        selectStep(step)
                      }
                    >
                      <span>{step.text}</span>
                      <strong>+</strong>
                    </button>
                  ))
                ) : (
                  <p className="order-actions-empty">
                    Ya has colocado todos los
                    pasos.
                  </p>
                )}
              </div>
            </div>

            <div className="order-actions-section">
              <div className="order-actions-section-header">
                <h3>Tu orden</h3>

                <span>
                  {selectedSteps.length}/
                  {round?.orderedSteps.length}
                </span>
              </div>

              <div className="order-actions-list">
                {selectedSteps.length > 0 ? (
                  selectedSteps.map(
                    (step, index) => (
                      <div
                        key={step.id}
                        className="order-actions-selected-item"
                      >
                        <span className="order-actions-position">
                          {index + 1}
                        </span>

                        <p>{step.text}</p>

                        <div className="order-actions-controls">
                          <button
                            type="button"
                            onClick={() =>
                              moveSelectedStep(
                                index,
                                -1
                              )
                            }
                            disabled={index === 0}
                            aria-label="Mover hacia arriba"
                          >
                            ↑
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              moveSelectedStep(
                                index,
                                1
                              )
                            }
                            disabled={
                              index ===
                              selectedSteps.length -
                                1
                            }
                            aria-label="Mover hacia abajo"
                          >
                            ↓
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              removeSelectedStep(
                                step,
                                index
                              )
                            }
                            aria-label="Retirar paso"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <p className="order-actions-empty">
                    Pulsa los pasos en el orden en
                    que deberían realizarse.
                  </p>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            className="order-actions-confirm"
            onClick={confirmSequence}
            disabled={
              selectedSteps.length !==
              round?.orderedSteps.length
            }
          >
            Confirmar secuencia
          </button>
        </section>
      </main>
    </div>
  );
}

export default OrderActions;